import { Component, OnInit, signal, computed, ViewChild, inject, DestroyRef, Injector, afterNextRender, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormBuilder, Validators, FormGroup, AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputLabel } from '../../parts/input-label/input-label.component';
import { AmountInput } from '../../parts/amount-input/amount-input.component';
import { ScheduleFrequency, ChartOfAccountCategory, PropertyStatuses } from '../../core/enums/data.enums';
import { BaseFormComponent } from '../../core/base-classes/base-form-component';
import { CustomValidators } from '../../core/validators/custom-validators';
import { FormState, InvoiceScheduleHelpText } from '../../core/enums/forms.enums';
import { UserSessionService } from '../../core/services/user-session.service';
import { DismissibleNotesService } from '../../core/services/dismissible-notes.service';
import { CreatePropertyChargeScheduleRequest, PropertyChargeScheduleModel, UpdateLateFeeConfigurationsRequest, UpdatePropertyChargeScheduleRequest } from '../../core/models/data/property-charge-schedule.model';
import { LateFeeUtilities } from '../../core/utilities/late-fee.utilities';
import { DateUtilities, DateFormat } from '../../core/utilities/date.utilities';
import { ListConstants } from '../../core/constants/lists.constants';
import { ChargesService } from '../../core/services/charge/charge.service';
import { ConfirmationModalComponent } from "../../parts/confirmation-modal/confirmation-modal.component";
import { InfoModalComponent } from "../../parts/info-modal/info-modal.component";
import { ModalActions, ModalTypes } from '../../core/enums/base.enums';
import { BootstrapService } from '../../core/services/bootstrap.service';
import { BalancesMessagingConstants, GenericMessagingConstants } from '../../core/constants/messaging.constants';
import { CurrencyUtilities } from '../../core/utilities/currency.utilities';
import { Router } from '@angular/router';
import { ChargeSchedulePropertySelectorComponent, PropertySelectionState } from './components/charge-schedule-property-selector/charge-schedule-property-selector.component';
import { ChargeScheduleAttachedPropertiesComponent } from './components/charge-schedule-attached-properties/charge-schedule-attached-properties.component';
import { LateFeesSectionComponent } from './components/late-fees/late-fees.component';
import { InvalidDateDetectorDirective } from '../../core/directives/invalid-date-detector.directive';
import { PaymentConstansts } from '../../core/constants/constants';
import { OrganizationSettingModel } from '../../core/models/data/organization-setting.model';
import { CanComponentDeactivate } from '../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../core/services/dirty-form-registry.service';
import { ChartOfAccountTypeaheadComponent } from '../../parts/chart-of-account-typeahead/chart-of-account-typeahead.component';
import { LocationService } from '../../core/services/location.service';
import { TimeZoneUtilities } from '../../core/utilities/timezone.utilities';
import { StringUtilities } from '../../core/utilities/string.utilities';
import { PageTitleComponent } from '../../parts/page-title/page-title.component';
import { StripeSetupNoticeComponent } from '../../parts/stripe-setup-notice/stripe-setup-notice.component';
import { DatePlaceholderDirective } from '../../core/directives/date-placeholder.directive';
import { TooltipDirective } from '../../core/directives/tooltip.directive';

@Component({
  selector: 'ms-charge-schedule-form',
  standalone: true,
  imports: [
    PageTitleComponent,
    CommonModule,
    ReactiveFormsModule,
    InputLabel,
    FontAwesomeModule,
    AmountInput,
    ChargeSchedulePropertySelectorComponent,
    ChargeScheduleAttachedPropertiesComponent,
    LateFeesSectionComponent,
    ConfirmationModalComponent,
    InfoModalComponent,
    InvalidDateDetectorDirective,
    ChartOfAccountTypeaheadComponent,
    DatePlaceholderDirective,
    StripeSetupNoticeComponent,
    TooltipDirective
],
  templateUrl: './charge-schedule.form.html',
  styleUrl: './charge-schedule.form.scss',
})
export class ChargeScheduleForm extends BaseFormComponent implements OnInit, CanComponentDeactivate {

  //#region Properties & Signals

  public formState = input<FormState>(FormState.Create);
  public scheduleData = input<PropertyChargeScheduleModel | undefined>(undefined);
  public openInvoicesCount = input<number>(0);

  public ChargeScheduleFrequency = ScheduleFrequency;
  public FormState = FormState;
  public ChartOfAccountCategory = ChartOfAccountCategory;
  public InvoiceScheduleHelpText = InvoiceScheduleHelpText;

  public chargeScheduleForm: FormGroup;

  public activeOrganizationSettings = signal<OrganizationSettingModel | undefined>(undefined);

  public propertiesFilter = ListConstants.PROPERTIES_FILTER_DEFAULT();
  public propertySelectionState: PropertySelectionState | null = null;
  public propertySelectionTouched = false;
  public editingProperties = signal<boolean>(false);
  public isPropertySelectionExpanded = signal<boolean>(false);
  private navigateToListOnInfoConfirm = false;
  // Set while resetForm() runs: a programmatic reset is never a user reactivation.
  private isResettingForm = false;

  public timezoneWarningMessage: string | undefined = undefined;
  public timezoneWarningDismissKey = BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_FORM_TIMEZONE_WARNING_DISMISS_KEY();
  public dismissibleNotesService = inject(DismissibleNotesService);
  public lateFeesLocked = signal<boolean>(false);

  public readonly frequencyOptions: string[] = Object.values(ScheduleFrequency);
  public readonly customFrequencyMonthsOptions: number[] = PaymentConstansts.CUSTOM_FREQUENCY_MONTHS_OPTIONS();

  @ViewChild('chargeScheduleConfirmationModalRef') chargeScheduleConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('chargeScheduleSaveConfirmationModalRef') chargeScheduleSaveConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('editPropertiesConfirmationModalRef') editPropertiesConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('chargeScheduleInfoModalRef') chargeScheduleInfoModalRef!: InfoModalComponent;
  @ViewChild('propertySelectorRef') propertySelectorComponent!: ChargeSchedulePropertySelectorComponent;
  @ViewChild(LateFeesSectionComponent) lateFeesSection?: LateFeesSectionComponent;

  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private dirtyFormRegistry = inject(DirtyFormRegistryService);
  private locationService = inject(LocationService);
  private issuanceDateValue = signal<string>('');
  private _dueDateValue = signal<string>('');
  public dueDateValue = this._dueDateValue.asReadonly();
  private frequencyValue = signal<ScheduleFrequency>(ScheduleFrequency.Once);
  // TODO: DEV-2596 -  Jake - Revisit when we allow organizations to pay service fees
  // private serviceFeesValue = signal<boolean>(true);
  // private initialMemberPaysServiceFee = signal<boolean | undefined>(undefined);

  // public showRetroactiveFeeWarning = computed<boolean>(() =>
  //   this.formState() === FormState.Update &&
  //   this.openInvoicesCount() > 0 &&
  //   this.initialMemberPaysServiceFee() !== undefined &&
  //   this.serviceFeesValue() !== this.initialMemberPaysServiceFee()
  // );

  // public retroactiveFeeWarningMessage = computed<string>(() =>
  //   BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_RETROACTIVE_FEE_WARNING().replace('{count}', String(this.openInvoicesCount()))
  // );

  public isIssuanceDateEndOfMonth = computed(() => {
    const dateValue = this.issuanceDateValue();
    if (!dateValue) return false;
    return DateUtilities.parseLocalDate(dateValue).getDate() > 27;
  });

  public isDueDateEndOfMonth = computed(() => {
    const dateValue = this.dueDateValue();
    if (!dateValue) return false;
    return DateUtilities.parseLocalDate(dateValue).getDate() > 27;
  });

  public shouldShowEndDate = computed(() => {
    return this.frequencyValue() !== ScheduleFrequency.Once;
  });

  public isCustomFrequency = computed(() => {
    return this.frequencyValue() === ScheduleFrequency.Custom;
  });

  //#endregion Properties & Signals

  //#region Constructor & Initialization

  constructor(
    private fb: FormBuilder,
    private chargesService: ChargesService,
    public userSessionService: UserSessionService,
    private bootstrapService: BootstrapService,
    private routerService: Router
  ) {
    super();

    this.chargeScheduleForm = this.fb.group({
      invoiceName: ['', [Validators.required, Validators.maxLength(250)]],
      description: ['', [Validators.maxLength(250)]],
      activateCharge: [true],
      // TODO: DEV-2596 -  Jake - Revisit when we allow organizations to pay service fees
      // serviceFees: [true],
      invoiceInstructions: ['', [Validators.maxLength(250)]],
      revenueType: [null, [Validators.required, Validators.min(1)]],
      assetType: [null, [Validators.required, Validators.min(1)]],
      amount: ['', [Validators.required, CustomValidators.validAmount, Validators.min(PaymentConstansts.INVOICE_SCHEDULE_AMOUNT_MINIMUM())]],
      currency: ['USD'],
      frequency: [ScheduleFrequency.Once, [Validators.required]],
      customFrequencyMonths: [null as number | null],
      issuanceDate: ['', [Validators.required, CustomValidators.futureDate('Issuance date cannot be in the past')]],
      dueDate: ['', [Validators.required, CustomValidators.futureDate('Due date cannot be in the past')]],
      endDate: ['', CustomValidators.futureDate('End date cannot be in the past')]
    }, { 
      validators: CustomValidators.dateRangeValidator(
        'issuanceDate',
        'dueDate',
        'endDate',
        'frequency',
        ScheduleFrequency.Once
      )
    });

    this.dirtyFormRegistry.register(this);
    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));
  }

  ngOnInit(): void {
    this.chargeScheduleForm.get('issuanceDate')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.issuanceDateValue.set(value || '');
        this.revalidateReactivationDates(false);
      });

    this.chargeScheduleForm.get('dueDate')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this._dueDateValue.set(value || '');
        this.revalidateReactivationDates(false);
      });

    this.chargeScheduleForm.get('endDate')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.revalidateReactivationDates(false);
      });

    this.chargeScheduleForm.get('frequency')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.frequencyValue.set(value || ScheduleFrequency.Once);

        // The API requires the month count for Custom and rejects it for every other frequency.
        const customFrequencyMonthsControl = this.chargeScheduleForm.get('customFrequencyMonths');
        if (value === ScheduleFrequency.Custom) {
          customFrequencyMonthsControl?.setValidators(Validators.required);
        } else {
          customFrequencyMonthsControl?.setValue(null);
          customFrequencyMonthsControl?.clearValidators();
        }
        customFrequencyMonthsControl?.updateValueAndValidity();

        const endDateControl = this.chargeScheduleForm.get('endDate');
        const activateCharge = !!this.chargeScheduleForm.get('activateCharge')?.value;
        if (value === ScheduleFrequency.Once) {
          endDateControl?.setValue('');
          endDateControl?.clearValidators();
        } else if (activateCharge) {
          endDateControl?.setValidators(CustomValidators.futureDate('End date cannot be in the past'));
        } else {
          endDateControl?.clearValidators();
        }
        endDateControl?.updateValueAndValidity();
        this.revalidateReactivationDates(false);
      });

    let previousActivateValue = this.chargeScheduleForm.get('activateCharge')?.value;
    this.chargeScheduleForm.get('activateCharge')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        const wasJustActivated = !this.isResettingForm && previousActivateValue === false && value === true;
        previousActivateValue = value;
        this.syncDateValidatorsForActivation(!!value);
        this.revalidateReactivationDates(wasJustActivated);
      });

    // TODO: DEV-2596 -  Jake - Revisit when we allow organizations to pay service fees
    // this.chargeScheduleForm.get('serviceFees')?.valueChanges
    //   .pipe(takeUntilDestroyed(this.destroyRef))
    //   .subscribe(value => {
    //     this.serviceFeesValue.set(!!value);
    //   });

    const schedule = this.scheduleData();
    if (this.formState() === FormState.Update && schedule) {
      this.populateFormForEdit(schedule);
      // lateFeesSection is a @ViewChild — not resolved yet at ngOnInit time, only after the view
      // initializes. Defer until then so populateLateFeesForEdit doesn't hit its null guard.
      afterNextRender(() => this.populateLateFeesForEdit(schedule), { injector: this.injector });
    }

    this.loadActiveOrganizationSettingsTimezone();
  }

  //#endregion Constructor & Initialization

  //#region Data Loading

  private populateLateFeesForEdit(schedule: PropertyChargeScheduleModel): void {
    if (!this.lateFeesSection) {
      return;
    }

    const today = DateUtilities.startOfToday();
    const issuanceDate = schedule.firstIssuanceDate
      ? DateUtilities.parseLocalDate(schedule.firstIssuanceDate)
      : null;
    const locked = issuanceDate !== null && issuanceDate <= today;

    this.lateFeesSection.populate(schedule);
    this.lateFeesSection.setReadOnly(locked);
    this.lateFeesLocked.set(locked);
  }

  public async loadActiveOrganizationSettingsTimezone(): Promise<void> {
    const activeOrganization = this.userSessionService.activeOrganization();
    const organizationId = activeOrganization?.organizationId;
    this.userSessionService.propertyChargeSchedulesFilter.organizationIds = organizationId ? [organizationId] : [];

    const orgSettings = await this.userSessionService.getOrganizationSettings();
    this.activeOrganizationSettings.set(orgSettings);

    const timezoneDisplayName = await this.resolveTimezoneDisplayName(orgSettings?.timeZoneId);
    const escapedTimezoneDisplayName = StringUtilities.escapeHtmlWithLineBreaks(timezoneDisplayName);
    this.timezoneWarningMessage = BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_FORM_TIMEZONE_WARNING().replace('{timezone}', `<b>${escapedTimezoneDisplayName}</b>`);
  }

  private async resolveTimezoneDisplayName(timeZoneId: string | undefined): Promise<string> {
    if (!timeZoneId) {
      return 'Unknown';
    }

    const result = await this.locationService.getTimezones();
    const timezone = result.data?.find(tz => tz.id === timeZoneId);
    return timezone ? TimeZoneUtilities.stripUtcOffset(timezone.displayName) : timeZoneId;
  }

  private populateFormForEdit(schedule: PropertyChargeScheduleModel): void {
    const amountInMajorUnits = CurrencyUtilities.convertMinorUnitsToMajor(
      schedule.scheduledAmount,
      schedule.scheduledCurrency?.minorUnits ?? this.userSessionService.currentMinorUnits()
    );

    // TODO: DEV-2596 -  Jake - Revisit when we allow organizations to pay service fees
    // this.initialMemberPaysServiceFee.set(schedule.memberPaysServiceFee);
    // this.serviceFeesValue.set(schedule.memberPaysServiceFee);

    this.chargeScheduleForm.patchValue({
      invoiceName: schedule.name,
      description: schedule.description || '',
      activateCharge: schedule.status === PropertyStatuses.Active,
      // TODO: DEV-2596 -  Jake - Revisit when we allow organizations to pay service fees
      // serviceFees: schedule.memberPaysServiceFee,
      invoiceInstructions: schedule.memo || '',
      amount: amountInMajorUnits,
      currency: schedule.scheduledCurrency?.code || 'USD',
      frequency: schedule.frequency,
      // Must follow frequency: patching frequency fires its valueChanges, which clears this control.
      customFrequencyMonths: schedule.customFrequencyMonths ?? null,
      issuanceDate: schedule.firstIssuanceDate,
      endDate: schedule.scheduleEnd || '',
      revenueType: schedule.revenueAccount.chartOfAccountId,
      assetType: schedule.assetAccount.chartOfAccountId,
    });

    const dueDateStr = this.computeDueDateFromSchedule(schedule);
    if (dueDateStr) {
      this.chargeScheduleForm.patchValue({ dueDate: dueDateStr });
    }

    this.syncDateValidatorsForActivation(schedule.status === PropertyStatuses.Active);
    this.chargeScheduleForm.markAsPristine();
  }

  private computeDueDateFromSchedule(schedule: PropertyChargeScheduleModel): string | undefined {
    if (!schedule.firstIssuanceDate) return undefined;
    const [year, month] = schedule.firstIssuanceDate.split('-').map(Number);
    const dueDate = new Date(year, month - 1 + schedule.invoiceDueNumberOfMonthsFromIssuance, 1);

    if (schedule.invoiceDueAtEndOfMonth) {
      dueDate.setMonth(dueDate.getMonth() + 1, 0);
    } else if (schedule.invoiceDueDayOfMonth) {
      dueDate.setDate(schedule.invoiceDueDayOfMonth);
    }

    return DateUtilities.format(dueDate, DateFormat.ISODate);
  }

  //#endregion Data Loading

  //#region Form Submission

  public onSubmit(): void {
    const isUpdate = this.formState() === FormState.Update;
    const requiresPropertySelection = !isUpdate || this.editingProperties();

    const lateFeesForm = this.lateFeesSection?.form;
    const lateFeesEditable = !this.lateFeesLocked();
    const lateFeesInvalid = lateFeesEditable
      && !!lateFeesForm
      && (lateFeesForm.invalid || (this.lateFeesSection?.hasAtLeastOneTierError() ?? false));

    if (this.chargeScheduleForm.invalid || lateFeesInvalid) {
      this.markAllFieldsAsTouched();
      lateFeesForm?.markAllAsTouched();
      if (lateFeesInvalid && this.lateFeesSection) {
        this.lateFeesSection.isExpanded.set(true);
      }
      if (requiresPropertySelection && (!this.propertySelectionState || !this.propertySelectionState.isValid)) {
        this.propertySelectionTouched = true;
      }
      return;
    }

    if (requiresPropertySelection && (!this.propertySelectionState || !this.propertySelectionState.isValid)) {
      this.propertySelectionTouched = true;
      return;
    }

    if (isUpdate) {
      this.chargeScheduleSaveConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.chargeScheduleSaveConfirmationModalRef.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_SAVE_CONFIRMATION());
      this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Show);
    } else {
      this.onSaveConfirmed(undefined);
    }
  }

  public onEditPropertiesRequested(): void {
    this.editPropertiesConfirmationModalRef.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_EDIT_PROPERTIES_CONFIRMATION());
    this.bootstrapService.manageModal("#chargeScheduleEditPropertiesConfirmationModal", ModalActions.Show);
  }

  public onEditPropertiesConfirmed(): void {
    this.bootstrapService.manageModal("#chargeScheduleEditPropertiesConfirmationModal", ModalActions.Hide);
    this.propertySelectionState = null;
    this.propertySelectionTouched = false;
    this.editingProperties.set(true);
  }

  public onEditPropertiesCancelled(): void {
    this.bootstrapService.manageModal("#chargeScheduleEditPropertiesConfirmationModal", ModalActions.Hide);
  }

  public async onSaveConfirmed(note: string | undefined): Promise<void> {
    const isUpdate = this.formState() === FormState.Update;
    const orgId = this.userSessionService.activeOrganization()?.organizationId;

    if (!isUpdate && !orgId) {
      this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Hide);
      return;
    }

    this.isSubmitting = true;
    this.chargeScheduleSaveConfirmationModalRef.isSubmitting.set(true);

    try {
      if (isUpdate) {
        await this.submitUpdate(note);
      } else {
        await this.submitCreate(orgId!);
      }
    } finally {
      this.isSubmitting = false;
      this.chargeScheduleSaveConfirmationModalRef.isSubmitting.set(false);
    }
  }

  public onSaveCancelled(): void {
    this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Hide);
  }

  private async submitCreate(organizationId: number): Promise<void> {
    try {
      const request = this.buildSubmissionRequest(organizationId);
      const result = await this.chargesService.createPropertyChargeSchedule(request);

      this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Hide);

      if (result.success) {
        this.submitSucceeded = true;
        this.chargeScheduleConfirmationModalRef.modalType.set(ModalTypes.Success);
        this.chargeScheduleConfirmationModalRef.message.set(result.message);
        this.chargeScheduleConfirmationModalRef.cancelButtonText.set('Leave');
        this.chargeScheduleConfirmationModalRef.confirmButtonText.set('Create Another');
        this.bootstrapService.manageModal("#chargeScheduleConfirmationModal", ModalActions.Show);
      } else {
        this.chargeScheduleInfoModalRef.modalType.set(ModalTypes.Failure);
        this.chargeScheduleInfoModalRef.message.set(result.message);
        this.bootstrapService.manageModal("#chargeScheduleInfoModal", ModalActions.Show);
      }
    } catch (error) {
      console.error('Exception caught:', error);
      this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Hide);
      this.chargeScheduleInfoModalRef.modalType.set(ModalTypes.Failure);
      this.chargeScheduleInfoModalRef.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_CREATE_FAILURE());
      this.bootstrapService.manageModal("#chargeScheduleInfoModal", ModalActions.Show);
    }
  }

  private async submitUpdate(note: string | undefined): Promise<void> {
    const schedule = this.scheduleData();
    if (!schedule) {
      this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Hide);
      return;
    }

    try {
      const request = this.buildUpdateRequest(note, schedule);
      const result = await this.chargesService.updatePropertyChargeSchedule(schedule.propertyChargeScheduleId, request);

      if (!result.success) {
        this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Hide);
        this.chargeScheduleInfoModalRef.modalType.set(ModalTypes.Failure);
        this.chargeScheduleInfoModalRef.message.set(result.message);
        this.bootstrapService.manageModal("#chargeScheduleInfoModal", ModalActions.Show);
        return;
      }

      const lateFeesEditable = !this.lateFeesLocked();
      const lateFeesDirty = this.lateFeesSection?.form.dirty ?? false;

      if (lateFeesEditable && lateFeesDirty) {
        const lateFeesRequest = this.buildLateFeesUpdateRequest();
        if (lateFeesRequest) {
          const lateFeesResult = await this.chargesService.updatePropertyChargeScheduleLateFees(
            schedule.propertyChargeScheduleId,
            lateFeesRequest
          );

          this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Hide);

          if (!lateFeesResult.success) {
            this.chargeScheduleInfoModalRef.modalType.set(ModalTypes.Failure);
            this.chargeScheduleInfoModalRef.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_LATE_FEES_UPDATE_FAILURE());
            this.bootstrapService.manageModal("#chargeScheduleInfoModal", ModalActions.Show);
            return;
          }
        }
      } else {
        this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Hide);
      }

      this.submitSucceeded = true;
      this.chargeScheduleInfoModalRef.modalType.set(ModalTypes.Success);
      this.chargeScheduleInfoModalRef.message.set(result.message);
      this.navigateToListOnInfoConfirm = true;
      this.bootstrapService.manageModal("#chargeScheduleInfoModal", ModalActions.Show);
    } catch (error) {
      console.error('Exception caught:', error);
      this.bootstrapService.manageModal("#chargeScheduleSaveConfirmationModal", ModalActions.Hide);
      this.chargeScheduleInfoModalRef.modalType.set(ModalTypes.Failure);
      this.chargeScheduleInfoModalRef.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_UPDATE_FAILURE());
      this.bootstrapService.manageModal("#chargeScheduleInfoModal", ModalActions.Show);
    }
  }

  private buildSubmissionRequest(organizationId: number): CreatePropertyChargeScheduleRequest {
    const formValue = this.chargeScheduleForm.value;

    const issuanceDate = DateUtilities.parseLocalDate(formValue.issuanceDate);
    const dueDate = DateUtilities.parseLocalDate(formValue.dueDate);
    const dueDay = dueDate.getDate();

    const monthsDiff = (dueDate.getFullYear() - issuanceDate.getFullYear()) * 12
                     + (dueDate.getMonth() - issuanceDate.getMonth());
    
    const amountString = String(formValue.amount || '0');
    const scheduledAmount = CurrencyUtilities.convertCurrencyStringToMinorUnits(amountString, this.userSessionService.currentMinorUnits());

    const firstIssuanceDate = formValue.issuanceDate;

    let scheduleEnd = null;
    if (formValue.endDate) {
      scheduleEnd = formValue.endDate;
    }

    const lateFeesPayload = this.lateFeesSection?.getValue();
    const lateFeesEnabled = !!lateFeesPayload?.enableLateFees;
    const lateFeeConfigurations = lateFeesEnabled && lateFeesPayload
      ? LateFeeUtilities.buildLateFeeApiConfigurations(lateFeesPayload.tiers)
      : undefined;

    const request: CreatePropertyChargeScheduleRequest = {
      organizationId,
      name: formValue.invoiceName,
      description: formValue.description || null,
      scheduledAmount,
      memo: formValue.invoiceInstructions || null,
      revenueAccount: formValue.revenueType,
      assetAccount: formValue.assetType,
      activate: formValue.activateCharge,
      // TODO: DEV-2596 -  Jake - Revisit when we allow organizations to pay service fees. Until then always true.
      memberPaysServiceFee: true,
      scheduleFrequency: formValue.frequency as ScheduleFrequency,
      customFrequencyMonths: this.getCustomFrequencyMonthsForRequest(),
      firstIssuanceDate,
      sendInvoiceAtEndOfMonth: this.isIssuanceDateEndOfMonth() && !(formValue.frequency === ScheduleFrequency.Once),
      invoiceDueNumberOfMonthsFromIssuance: monthsDiff,
      invoiceDueDayOfMonth: this.isDueDateEndOfMonth() ? undefined : dueDay,
      invoiceDueAtEndOfMonth: this.isDueDateEndOfMonth(),
      scheduleEnd,
      properties: {
        useFilter: this.propertySelectionState!.useFilter,
        filter: this.propertySelectionState!.filter,
        selectedIds: this.propertySelectionState!.selectedIds,
        excludedIds: this.propertySelectionState!.excludedIds,
        note: null
      },
      enableLateFees: lateFeesEnabled,
      lateFeeRevenueAccount: lateFeesEnabled ? lateFeesPayload!.lateFeeRevenueAccount : undefined,
      lateFeeConfigurations,
    };

    return request;
  }

  private buildLateFeesUpdateRequest(): UpdateLateFeeConfigurationsRequest | null {
    const lateFeesPayload = this.lateFeesSection?.getValue();
    if (!lateFeesPayload) return null;

    if (!lateFeesPayload.enableLateFees) {
      return { enableLateFees: false };
    }

    return {
      enableLateFees: true,
      lateFeeRevenueAccount: lateFeesPayload.lateFeeRevenueAccount,
      lateFeeConfigurations: LateFeeUtilities.buildLateFeeApiConfigurations(lateFeesPayload.tiers),
    };
  }

  private buildUpdateRequest(note: string | undefined, schedule: PropertyChargeScheduleModel): UpdatePropertyChargeScheduleRequest {
    const formValue = this.chargeScheduleForm.value;

    const issuanceDate = DateUtilities.parseLocalDate(formValue.issuanceDate);
    const dueDate = DateUtilities.parseLocalDate(formValue.dueDate);
    const dueDay = dueDate.getDate();

    const monthsDiff = (dueDate.getFullYear() - issuanceDate.getFullYear()) * 12
                     + (dueDate.getMonth() - issuanceDate.getMonth());

    const amountString = String(formValue.amount || '0');
    const scheduledAmount = CurrencyUtilities.convertCurrencyStringToMinorUnits(amountString, this.userSessionService.currentMinorUnits());

    const firstIssuanceDate = formValue.issuanceDate;

    let scheduleEnd = null;
    if (formValue.endDate) {
      scheduleEnd = formValue.endDate;
    }

    const propertiesRequest = this.editingProperties() && this.propertySelectionState
      ? {
          useFilter: this.propertySelectionState.useFilter,
          filter: this.propertySelectionState.filter,
          selectedIds: this.propertySelectionState.selectedIds,
          excludedIds: this.propertySelectionState.excludedIds,
          note: null
        }
      : {
          useFilter: false,
          filter: null,
          selectedIds: schedule.propertyIds ?? [],
          excludedIds: null,
          note: null
        };

    const request: UpdatePropertyChargeScheduleRequest = {
      name: formValue.invoiceName,
      description: formValue.description || undefined,
      scheduledAmount,
      memo: formValue.invoiceInstructions || undefined,
      revenueAccount: formValue.revenueType,
      assetAccount: formValue.assetType,
      activate: formValue.activateCharge,
      // TODO: DEV-2596 -  Jake - Revisit when we allow organizations to pay service fees. Until then always true.
      memberPaysServiceFee: true,
      scheduleFrequency: formValue.frequency as ScheduleFrequency,
      customFrequencyMonths: this.getCustomFrequencyMonthsForRequest(),
      firstIssuanceDate,
      sendInvoiceAtEndOfMonth: this.isIssuanceDateEndOfMonth() && !(formValue.frequency === ScheduleFrequency.Once),
      invoiceDueNumberOfMonthsFromIssuance: monthsDiff,
      invoiceDueDayOfMonth: this.isDueDateEndOfMonth() ? undefined : dueDay,
      invoiceDueAtEndOfMonth: this.isDueDateEndOfMonth(),
      scheduleEnd: scheduleEnd ?? undefined,
      properties: propertiesRequest,
      note: note || undefined
    };

    return request;
  }

  private getCustomFrequencyMonthsForRequest(): number | null {
    const formValue = this.chargeScheduleForm.value;
    return formValue.frequency === ScheduleFrequency.Custom
      ? formValue.customFrequencyMonths ?? null
      : null;
  }

  //#endregion Form Submission

  //#region UI Helper Methods

  public onPropertySelectionChanged(state: PropertySelectionState): void {
    this.propertySelectionState = state;
    if (state.isValid) {
      this.propertySelectionTouched = false;
    }
  }

  public togglePropertySelectionExpanded(): void {
    this.isPropertySelectionExpanded.update(expanded => !expanded);
  }

  // Collapsed-header preview text, mirroring the Message Center recipient selector's collapsed summary.
  public get collapsedPropertySelectionSummary(): string {
    const state = this.propertySelectionState;
    if (!state) {
      return '';
    }
    if (state.useFilter) {
      return `All ${state.selectedCount} properties matching the current filter`;
    }
    return state.selectedItems.map(item => item.label).join(', ');
  }

  public onCancel(): void {
    if (this.chargeScheduleForm.dirty) {
      this.chargeScheduleConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.chargeScheduleConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal("#chargeScheduleConfirmationModal", ModalActions.Show);
    } else {
      this.routerService.navigate(['/invoice-schedules']);
    }
  }

  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.chargeScheduleForm.dirty && !this.submitSucceeded, () => {
      this.chargeScheduleConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.chargeScheduleConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal("#chargeScheduleConfirmationModal", ModalActions.Show);
    });
  }

  public resetForm(): void {
    this.isResettingForm = true;
    try {
      this.submitSucceeded = false;
      this.chargeScheduleForm.reset({
        activateCharge: true,
        // TODO: DEV-2596 -  Jake - Revisit when we allow organizations to pay service fees
        // serviceFees: true,
        frequency: ScheduleFrequency.Once,
        customFrequencyMonths: null,
        currency: 'USD',
        revenueType: null,
        assetType: null,
      });
      this.propertySelectionState = null;
      this.propertySelectionTouched = false;
      this.propertySelectorComponent?.resetSelection();
      this.lateFeesSection?.reset();
    } finally {
      this.isResettingForm = false;
    }
  }

  private markAllFieldsAsTouched(): void {
    this.chargeScheduleForm.markAllAsTouched();
  }

  // Deactivating an already-active schedule via the Edit form must not be blocked by past-date validation
  // — a schedule whose issuance date is already in the past should still be deactivatable. When activate
  // flips to false we drop the futureDate validator; when it flips back to true we re-apply it so a real
  // reactivation still requires future dates.
  private syncDateValidatorsForActivation(activate: boolean): void {
    const issuanceControl = this.chargeScheduleForm.get('issuanceDate');
    const dueControl = this.chargeScheduleForm.get('dueDate');
    const endControl = this.chargeScheduleForm.get('endDate');
    const frequency = this.chargeScheduleForm.get('frequency')?.value;

    if (activate) {
      issuanceControl?.setValidators([Validators.required, CustomValidators.futureDate('Issuance date cannot be in the past')]);
      dueControl?.setValidators([Validators.required, CustomValidators.futureDate('Due date cannot be in the past')]);
      if (frequency !== ScheduleFrequency.Once) {
        endControl?.setValidators(CustomValidators.futureDate('End date cannot be in the past'));
      } else {
        endControl?.clearValidators();
      }
    } else {
      issuanceControl?.setValidators([Validators.required]);
      dueControl?.setValidators([Validators.required]);
      endControl?.clearValidators();
    }

    issuanceControl?.updateValueAndValidity({ emitEvent: false });
    dueControl?.updateValueAndValidity({ emitEvent: false });
    endControl?.updateValueAndValidity({ emitEvent: false });
  }

  private revalidateReactivationDates(showPopup: boolean): void {
    if (this.formState() !== FormState.Update) return;

    const issuanceControl = this.chargeScheduleForm.get('issuanceDate');
    const dueControl = this.chargeScheduleForm.get('dueDate');
    const endControl = this.chargeScheduleForm.get('endDate');
    const activateCharge = this.chargeScheduleForm.get('activateCharge')?.value;
    const frequency = this.chargeScheduleForm.get('frequency')?.value;

    if (!activateCharge) {
      this.clearReactivationError(issuanceControl);
      this.clearReactivationError(dueControl);
      this.clearReactivationError(endControl);
      return;
    }

    const isOnce = frequency === ScheduleFrequency.Once;
    const issuanceInvalid = isOnce && DateUtilities.isPastDate(issuanceControl?.value);
    const dueInvalid = isOnce && DateUtilities.isPastDate(dueControl?.value);
    const endInvalid = DateUtilities.isPastDate(endControl?.value);

    this.applyReactivationError(issuanceControl, issuanceInvalid);
    this.applyReactivationError(dueControl, dueInvalid);
    this.applyReactivationError(endControl, endInvalid);

    if (showPopup && (issuanceInvalid || dueInvalid || endInvalid)) {
      this.chargeScheduleInfoModalRef?.modalType.set(ModalTypes.Failure);
      this.chargeScheduleInfoModalRef?.message.set(BalancesMessagingConstants.PROPERTY_CHARGE_SCHEDULE_REACTIVATION_PAST_DATES());
      this.bootstrapService.manageModal("#chargeScheduleInfoModal", ModalActions.Show);
    }
  }

  private applyReactivationError(control: AbstractControl | null | undefined, shouldFlag: boolean): void {
    if (!control) return;
    if (shouldFlag) {
      control.setErrors({ ...(control.errors || {}), pastDateOnReactivation: true });
      control.markAsTouched();
    } else {
      this.clearReactivationError(control);
    }
  }

  private clearReactivationError(control: AbstractControl | null | undefined): void {
    if (!control?.errors) return;
    const { pastDateOnReactivation, ...rest } = control.errors;
    control.setErrors(Object.keys(rest).length > 0 ? rest : null);
  }

  //#endregion UI Helper Methods

  //#region Modal Management

  // Leaving destroys this view, so only drop dirty — a reset would fire valueChanges (and their
  // modals) on the way out, and a modal shown in the same tick as a navigation is orphaned onto
  // <body> by Bootstrap. Only "Create Another" keeps the form mounted and needs a real reset.
  public confirmationConfirmFunction(): void {
    if (this.resolvePendingLeave(true)) {
      this.chargeScheduleForm.markAsPristine();
      this.bootstrapService.manageModal("#chargeScheduleConfirmationModal", ModalActions.Hide);
      return;
    }

    if (this.chargeScheduleConfirmationModalRef.modalType() === ModalTypes.Success) {
      this.resetForm();
      this.bootstrapService.manageModal("#chargeScheduleConfirmationModal", ModalActions.Hide);
      return;
    }

    this.chargeScheduleForm.markAsPristine();
    this.bootstrapService.manageModal("#chargeScheduleConfirmationModal", ModalActions.Hide);
    this.routerService.navigate(["/invoice-schedules"]);
  }

  public confirmationCancelFunction(): void {
    if (this.resolvePendingLeave(false)) {
      this.bootstrapService.manageModal("#chargeScheduleConfirmationModal", ModalActions.Hide);
      return;
    }

    if (this.chargeScheduleConfirmationModalRef.modalType() === ModalTypes.Success) {
      this.chargeScheduleForm.markAsPristine();
      this.bootstrapService.manageModal("#chargeScheduleConfirmationModal", ModalActions.Hide);
      this.routerService.navigate(["/invoice-schedules"]);
      return;
    }

    this.bootstrapService.manageModal("#chargeScheduleConfirmationModal", ModalActions.Hide);
  }

  public infoModalConfirmed(): void {
    this.bootstrapService.manageModal("#chargeScheduleInfoModal", ModalActions.Hide);
    if (this.navigateToListOnInfoConfirm) {
      this.navigateToListOnInfoConfirm = false;
      this.routerService.navigate(['/invoice-schedules']);
    }
  }

  //#endregion Modal Management

}
